import React, { createContext, useCallback, useContext, useState } from 'react';

type AppContextType = {
  title: React.ReactNode | null;
  addTitle: (node: React.ReactNode) => string;
  removeTitle: (id: string) => void;
};

type TitleEntry = { id: string; node: React.ReactNode };

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [titles, setTitles] = useState<TitleEntry[]>([]);

  const addTitle = useCallback((node: React.ReactNode) => {
    const id = crypto.randomUUID();
    setTitles(prev => [...prev, { id, node }]);
    return id;
  }, []);

  const removeTitle = useCallback((id: string) => {
    setTitles(prev => prev.filter(entry => entry.id !== id));
  }, []);

  const contextValue = React.useMemo<AppContextType>(
    () => ({
      addTitle,
      removeTitle,

      title: titles.at(-1)?.node || null,
    }),
    [titles, addTitle, removeTitle]
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
